import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Phone, AlertTriangle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { logoutAndRedirect } from "@/lib/logout";
import { DnaLogo } from "@/components/brand/DnaLogo";

export const PORTAL_PHONE = "703-821-0000";
export const PORTAL_EMAIL = "authority@donnormanassociates.com";

// ─── Portal Header ─────────────────────────────────────────────────────────────
export function PortalHeader({ onLogout }: { onLogout?: () => void }) {
  return (
    <header
      style={{
        background: "#1B2B4B",
        padding: "14px 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            background: "white",
            borderRadius: 8,
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DnaLogo height={28} />
        </div>
      </div>
      <span
        style={{
          color: "rgba(255,255,255,0.55)",
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        MEMBER PORTAL
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span
          style={{
            color: "rgba(255,255,255,0.65)",
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Phone size={14} style={{ opacity: 0.5 }} />
          {PORTAL_PHONE}
        </span>
        <a
          href="/portal/guide"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: "none",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "rgba(255,255,255,0.6)",
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: 6,
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          User Guide
        </a>
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.6)",
              fontSize: 12,
              padding: "4px 10px",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        )}
      </div>
    </header>
  );
}

// ─── Step Indicator ────────────────────────────────────────────────────────────
export function StepIndicator({ current, total = 5 }: { current: number; total?: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 28,
      }}
    >
      {Array.from({ length: total }, (_, i) => i + 1).map((n, i) => (
        <div key={n} style={{ display: "flex", alignItems: "center" }}>
          {i > 0 && (
            <div
              style={{
                height: 2,
                width: 56,
                background: current > n - 1 ? "#16a34a" : "#e2ddd6",
              }}
            />
          )}
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: `2px solid ${current > n ? "#16a34a" : current === n ? "#1B2B4B" : "#e2ddd6"}`,
              background: current > n ? "#16a34a" : current === n ? "#1B2B4B" : "white",
              color: current >= n ? "white" : "#6b7280",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              zIndex: 1,
              flexShrink: 0,
            }}
          >
            {current > n ? "✓" : n}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────
export function PortalHero({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <div
      style={{
        background: "#1B2B4B",
        padding: "56px 40px 80px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,150,42,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "#C9962A",
          marginBottom: 14,
        }}
      >
        {eyebrow}
      </div>
      <h1
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(32px, 5vw, 52px)",
          fontWeight: 700,
          color: "white",
          lineHeight: 1.12,
          marginBottom: 16,
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            color: "rgba(255,255,255,0.55)",
            fontSize: 16,
            maxWidth: 480,
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── Card Wrap ─────────────────────────────────────────────────────────────────
export function CardWrap({
  children,
  maxWidth = 780,
}: {
  children: React.ReactNode;
  maxWidth?: number;
}) {
  return (
    <div
      style={{
        padding: "0 24px 64px",
        maxWidth,
        margin: "-48px auto 0",
        position: "relative",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(27,43,75,0.18)",
          padding: "clamp(24px, 5vw, 40px) clamp(20px, 6vw, 48px)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Portal page shell ─────────────────────────────────────────────────────────
export function PortalPage({
  children,
  onLogout,
}: {
  children: React.ReactNode;
  onLogout?: () => void;
}) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F7F4EE",
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap"
      />
      <PortalHeader onLogout={onLogout} />
      {children}
    </div>
  );
}

// ─── Inline error block ────────────────────────────────────────────────────────
export function InlineError({ message }: { message: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#fee2e2",
        border: "1px solid #fca5a5",
        borderRadius: 8,
        padding: "10px 14px",
        marginBottom: 16,
        fontSize: 14,
        color: "#dc2626",
      }}
    >
      <svg
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {message}
    </div>
  );
}

// ─── Gold button ───────────────────────────────────────────────────────────────
export function GoldButton({
  children,
  onClick,
  disabled,
  style = {},
  type = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "15px 24px",
        background: disabled ? "#d4ccc0" : "#C9962A",
        color: "white",
        border: "none",
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontFamily: "inherit",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ─── Unauthorized screen ────────────────────────────────────────────────────────
export function UnauthorizedScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <PortalPage onLogout={onLogout}>
      <div
        style={{
          minHeight: "calc(100vh - 63px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 480 }}>
          <div
            style={{
              width: 64,
              height: 64,
              background: "#fee2e2",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <AlertTriangle size={28} color="#dc2626" />
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 26,
              fontWeight: 700,
              color: "#1B2B4B",
              marginBottom: 12,
            }}
          >
            Access Restricted
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 20, lineHeight: 1.6 }}>
            This portal is for authorized Service Bureau clients only. Your account does not have
            access to this area.
          </p>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24 }}>
            Contact us: <strong>{PORTAL_PHONE}</strong> · <strong>{PORTAL_EMAIL}</strong>
          </p>
          <button
            onClick={onLogout}
            style={{
              padding: "12px 28px",
              background: "#1B2B4B",
              color: "white",
              border: "none",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </PortalPage>
  );
}

// ─── Not Configured screen (service_bureau with no linked contact) ──────────────
export function NotConfiguredScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <PortalPage onLogout={onLogout}>
      <div
        style={{
          minHeight: "calc(100vh - 63px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 480 }}>
          <div
            style={{
              width: 64,
              height: 64,
              background: "#fef3c7",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              fontSize: 28,
            }}
          >
            ⚙️
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 26,
              fontWeight: 700,
              color: "#1B2B4B",
              marginBottom: 12,
            }}
          >
            Account Setup Incomplete
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
            Your account is not fully configured. Please contact support at{" "}
            <strong>{PORTAL_PHONE}</strong>.
          </p>
          <button
            onClick={onLogout}
            style={{
              padding: "12px 28px",
              background: "#1B2B4B",
              color: "white",
              border: "none",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </PortalPage>
  );
}

// ─── PortalGuard ───────────────────────────────────────────────────────────────
// Wraps portal pages. Enforces role-based access:
// - Unauth → /portal/login
// - admin/staff → / (main app)
// - direct_client → UnauthorizedScreen (403)
// - service_bureau with no companyId → NotConfiguredScreen
// - service_bureau ✓ → renders children with user passed down
export interface PortalUser {
  id: string;
  role: string;
  companyId: number;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export function PortalGuard({
  children,
}: {
  children: (props: { onLogout: () => void; portalUser: PortalUser }) => React.ReactNode;
}) {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<
    "loading" | "ok" | "unauthorized" | "not_configured"
  >("loading");
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);

  const handleLogout = async () => {
    try {
      await logoutAndRedirect("/portal/login");
    } catch (err) {
      console.warn("[PortalGuard] Logout request failed:", err);
      setLocation("/portal/login");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/auth/user", { credentials: "include" });
        // Initial unauthenticated access → plain login (no expired flag)
        if (res.status === 401 || !res.ok) {
          setLocation("/portal/login");
          return;
        }
        const user = await res.json();
        if (!user) {
          setLocation("/portal/login");
          return;
        }

        const role = user.role as string;

        if (role === "admin" || role === "staff") {
          window.location.href = "/";
          return;
        }

        if (role === "direct_client") {
          setStatus("unauthorized");
          return;
        }

        if (role !== "service_bureau") {
          setLocation("/portal/login");
          return;
        }

        // service_bureau — verify linked contact record via backend check
        const configRes = await fetch("/api/portal/check-config", { credentials: "include" });
        const configData = configRes.ok ? await configRes.json() : { configured: false };
        if (!configData.configured) {
          setStatus("not_configured");
          return;
        }

        setPortalUser({
          id: user.id,
          role: user.role,
          companyId: user.companyId,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        });
        setStatus("ok");
      } catch {
        setLocation("/portal/login");
      }
    })();
  }, []);

  if (status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F7F4EE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: "3px solid #e2ddd6",
            borderTopColor: "#1B2B4B",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>
    );
  }

  if (status === "unauthorized") {
    return <UnauthorizedScreen onLogout={handleLogout} />;
  }

  if (status === "not_configured") {
    return <NotConfiguredScreen onLogout={handleLogout} />;
  }

  return <>{children({ onLogout: handleLogout, portalUser: portalUser! })}</>;
}
